"use client";
// ════════════════════════════════════════════════════════════════════════════
// LƯỚI CUỐI — BOUNDARY Ở TẦNG GỐC (bắt cả lỗi của RootLayout).
//
// 🩸 VÌ SAO CÓ FILE NÀY (06/08/2026, sau khi lỗi lệch phiên bản NỔ LẠI ở /inventory/items).
// `app/error.tsx` chỉ bọc phần DƯỚI root layout. Nhưng gói RSC mà server action trả về LUÔN dựng
// lại từ GỐC (`isRootRender`) ⇒ RootLayout (Sidebar · CommandPalette · MobileNav…) render lại bằng
// mã mô-đun của bản MỚI ⇒ tab cũ nổ NGAY TRÊN ĐẦU `app/error.tsx`, chỗ nó với không tới.
//
// Không có file này thì Next dùng boundary MẶC ĐỊNH, và `react-client-callbacks/
// error-boundary-callbacks.js` coi boundary mặc định là "ngầm" nên xử như lỗi CHƯA-BẮT: xé cả cây
// React rồi mới bắn lỗi ra `window`. Hậu quả đo được:
//   ① người dùng nhận màn "Application error: a client-side exception has occurred" — tiếng Anh,
//      KHÔNG một nút nào. Nhân viên phòng khám chỉ còn nước đóng tab.
//   ② cây bị xé TRƯỚC khi lỗi bắn ra ⇒ `useEffect` cleanup đã gỡ listener của `ChunkReloadGuard`
//      lẫn `ClientErrorReporter` ⇒ không ai tự chữa, mà vé báo về hộp lỗi cũng có thể mất.
//      Đây đúng là lý do bản vá 05/08 "đã vá mà vẫn nổ": cả hai tay chữa đều nằm TRONG cây React.
//
// Chốt CHẶN TRƯỚC nằm ở `components/ChunkReloadGuard.tsx` (soi dấu bản trên đáp án server action).
// File này là LƯỚI CUỐI cho những gì vẫn lọt — và cho MỌI lỗi tầng gốc khác, để không bao giờ còn
// màn tiếng Anh cụt đường.
//
// ⚠️ VIẾT BẰNG THẺ TRẦN + STYLE NỘI TUYẾN, KHÔNG dùng `@/components/ui`, KHÔNG dùng lớp Tailwind.
// Đây là lưới CUỐI: nó chạy đúng lúc "một mô-đun nào đó của bản mới không nạp được" — kéo thêm
// component/CSS vào đây là tự đặt cược rằng đúng mô-đun ấy còn lành. Thẻ trần thì không cược gì cả.
// (Next đòi global-error phải tự dựng <html>/<body> vì nó THAY cả root layout.)
// ════════════════════════════════════════════════════════════════════════════
import { useEffect, useState } from "react";
import { clientErrorReporter } from "@/components/ClientErrorReporter";
import { loiKemMaSuCo } from "@/lib/loi/ma-su-co";
import { laLoiLechPhienBan, taiLaiThuCong, tuTaiLaiMotLan } from "@/lib/loi/lech-phien-ban";
import { nenDeVeLoi, phanLoaiCongGac, TIEU_DE_CONG_GAC } from "@/lib/loi/phien-dang-nhap";

export default function GlobalError({
  error,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const lechPhienBan = laLoiLechPhienBan(error);

  // CỔNG GÁC CHẶN (phiên hụt giữa lúc gửi server action) — KHÔNG phải app hỏng.
  // 🩸 VÉ THẬT 02/09/2026 ở `/` (bản 83e6de4) rơi ĐÚNG vào đây: gói RSC của server action luôn dựng
  // lại từ GỐC nên lỗi nổ TRÊN ĐẦU app/error.tsx, lưới cuối này hứng. Bản trước vứt `error.message`
  // đi rồi bày câu "Trang gặp trục trặc… báo kỹ thuật kèm mã sự cố": người dùng không hề biết mình
  // chỉ cần đăng nhập lại, còn hộp lỗi thì nhận một phiếu 🟠 cho một chuyện bình thường.
  const congGac = phanLoaiCongGac(error);
  const [dangChuyenLogin, setDangChuyenLogin] = useState(false);

  useEffect(() => {
    console.error("[app] lỗi tầng gốc:", error?.message, error?.digest);
    // BÁO TAY về hộp lỗi: tới được đây nghĩa là cây React sắp/đã bị xé, không trông vào listener
    // của `ClientErrorReporter` được nữa. `clientErrorReporter` là instance ở module scope nên
    // `report()` vẫn chạy dù `install()` đã bị gỡ. Nhãn `global-error` để người đọc vé biết ngay
    // lỗi nổ ở TẦNG GỐC (khác `error-boundary` = dưới root layout) — hai chỗ này chữa khác nhau.
    // 🚫 HẾT PHIÊN KHÔNG PHẢI BỌ ⇒ KHÔNG ĐẺ VÉ (luật + bằng chứng vé thật ở `nenDeVeLoi`).
    // Ca "máy chủ đăng nhập bận" thì VẪN báo, kèm nhãn để người đọc vé khỏi chẩn đoán lại từ đầu.
    if (nenDeVeLoi(error)) {
      clientErrorReporter.report(loiKemMaSuCo(error), {
        source:
          `global-error${lechPhienBan ? ":lech-phien-ban" : ""}${congGac ? `:${congGac}` : ""}` +
          `${error?.digest ? `:${error.digest}` : ""}`,
      });
    }
    // Lệch phiên bản → `reset()` là NÚT CHẾT (sổ mô-đun trong bộ nhớ vẫn thiếu) ⇒ tự nạp bản mới.
    // Cạn ngân sách thì thôi, người dùng vẫn còn nút bấm bên dưới.
    if (lechPhienBan) tuTaiLaiMotLan();
  }, [error, lechPhienBan, congGac]);

  return (
    <html lang="vi">
      <body
        style={{
          margin: 0,
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          padding: "24px",
          background: "#f8fafc",
          color: "#0f172a",
          fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
        }}
      >
        <div
          style={{
            maxWidth: "480px",
            width: "100%",
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            padding: "28px 24px",
            textAlign: "center",
            boxShadow: "0 1px 3px rgba(15,23,42,.08)",
          }}
        >
          <h1 style={{ margin: "0 0 8px", fontSize: "18px", fontWeight: 600 }}>
            {congGac
              ? TIEU_DE_CONG_GAC[congGac]
              : lechPhienBan
                ? "Phần mềm vừa được cập nhật"
                : "Đã có lỗi xảy ra"}
          </h1>
          <p style={{ margin: "0 0 20px", fontSize: "14px", lineHeight: 1.6, color: "#475569" }}>
            {/* Câu của cổng gác hiện NGUYÊN VĂN: nó đã nói đủ "thao tác vừa rồi CHƯA được lưu" +
                phải làm gì tiếp. Chép tay bản thứ hai ở đây là tự tạo hai nguồn sự thật. */}
            {congGac
              ? error.message
              : lechPhienBan
                ? "Bản mới vừa lên trong lúc bạn đang mở trang. Trang sẽ tự tải lại sau vài giây — nếu không, bấm nút bên dưới. Việc bạn vừa bấm đã được lưu."
                : "Trang gặp trục trặc. Hãy bấm Tải lại trang; nếu vẫn lỗi, báo kỹ thuật kèm mã sự cố bên dưới."}
          </p>
          {/* LUÔN có nút — kể cả khi đang tự tải lại. Lượt tải lại có thể KHÔNG bao giờ xảy ra
              (người dùng bấm Esc, hoặc trang có chốt "chưa lưu" và họ chọn Ở LẠI); màn chờ không
              nút là ngõ cụt, chỉ còn nước đóng tab. Bấm tay thì BỎ QUA ngân sách tự-tải-lại.
              HẾT PHIÊN thì nút phải đưa về /login — "Tải lại trang" ở ca đó tuy cũng tới được
              trang đăng nhập (cổng gác đá GET về /login) nhưng CHỮ TRÊN NÚT nói sai việc, người
              dùng bấm mà không hiểu vì sao mình bị hỏi mật khẩu. Điều hướng CỨNG cho cả hai ca:
              đây là lưới cuối, cây React đã hỏng, đừng trông vào router. */}
          <button
            type="button"
            onClick={() => {
              if (congGac === "het-phien") {
                setDangChuyenLogin(true);
                window.location.assign("/login");
                return;
              }
              taiLaiThuCong();
            }}
            disabled={dangChuyenLogin}
            style={{
              cursor: dangChuyenLogin ? "progress" : "pointer",
              opacity: dangChuyenLogin ? 0.6 : 1,
              border: "1px solid #0f172a",
              background: "#0f172a",
              color: "#fff",
              borderRadius: "8px",
              padding: "9px 18px",
              fontSize: "14px",
              fontWeight: 600,
              fontFamily: "inherit",
            }}
          >
            {congGac === "het-phien"
              ? dangChuyenLogin
                ? "Đang chuyển…"
                : "Đăng nhập lại"
              : "Tải lại trang"}
          </button>
          {error?.digest ? (
            <p style={{ margin: "16px 0 0", fontSize: "11px", color: "#94a3b8" }}>
              Mã sự cố: {error.digest}
            </p>
          ) : null}
        </div>
      </body>
    </html>
  );
}
